import { Component, Input, OnInit, OnDestroy, OnChanges, Output, EventEmitter } from '@angular/core';

import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Observable } from 'rxjs/Observable';

import { parse } from 'date-fns';

import { DataTableSource } from '../../../classes/DataTableSource/datatablesource';

import {
    CollegeInfo,
    CollegeTermInfo,
    ProgramInfo,
    SchoolCounselorInfo,
    StudentCollegeInfo,
} from '../../../services/application/application.service';
import { DialogService } from '../../../services/dialogs/dialogs.service';
import { UserInfo } from '../../../services/user/user.service';
import { FormStatusPipe } from '../../../pipes/formstatus.pipe';
import { MatSelectChange } from '@angular/material';

@Component({
    selector: 'app-student-college-records',
    styleUrls: ['studentcollegerecords.component.css'],
    templateUrl: 'studentcollegerecords.component.html',
})
export class StudentCollegeRecordsComponent implements OnInit, OnDestroy, OnChanges {
    // Private
    private AllColumns: string[] = ['Selection', 'CollegeName', 'DEProgram', 'Term', 'Counselor', 'Status', 'Actions'];

    // Inputs
    @Input() AcceleratedDegreePrograms: ProgramInfo[];
    @Input() AvailableColleges: CollegeInfo[];
    @Input() Counselors: SchoolCounselorInfo[];
    @Input() DualEnrollmentPrograms: ProgramInfo[];
    @Input() SelectedCollege: StudentCollegeInfo;
    @Input() SelectedSchoolYear: string;
    @Input() ShowAgreements: boolean;
    @Input() SmallDisplay: boolean;
    @Input() User: UserInfo;
    @Input() StudentCollegeRecords: StudentCollegeInfo[];

    // Outputs
    @Output() add: EventEmitter<StudentCollegeInfo> = new EventEmitter<StudentCollegeInfo>();
    @Output() cancel: EventEmitter<StudentCollegeInfo> = new EventEmitter<StudentCollegeInfo>();
    @Output() change: EventEmitter<StudentCollegeInfo> = new EventEmitter<StudentCollegeInfo>();
    @Output() programChange: EventEmitter<MatSelectChange> = new EventEmitter<MatSelectChange>();
    @Output() remove: EventEmitter<StudentCollegeInfo> = new EventEmitter<StudentCollegeInfo>();
    @Output() select: EventEmitter<StudentCollegeInfo> = new EventEmitter<StudentCollegeInfo>();
    @Output() toggleAgreements: EventEmitter<StudentCollegeInfo> = new EventEmitter<StudentCollegeInfo>();
    @Output() update: EventEmitter<StudentCollegeInfo> = new EventEmitter<StudentCollegeInfo>();
    @Output() updateprogram: EventEmitter<StudentProgramChange> = new EventEmitter<StudentProgramChange>();

    // Class
    AsAdvisor: boolean;
    AsCollegeAdmin: boolean;
    AsCollegeSupport: boolean;
    AsCounselor: boolean;
    AsDeveloper: boolean;
    AsDistrictAdmin: boolean;
    AsParent: boolean;
    AsStudent: boolean;
    DisplayColumns: string[];
    NoData: boolean;
    SCRecordDataSource: DataTableSource<StudentCollegeInfoUI>;
    ShownColumns: string[];
    StudentCollegeRecs: StudentCollegeInfoUI[];
    VisibleHeaders: string[];

    constructor(private dialogService: DialogService, private formStatusPipe: FormStatusPipe) {}

    ngOnDestroy() {}

    ngOnInit() {}

    ngOnChanges() {
        if (this.StudentCollegeRecords) {
            this.StudentCollegeRecs = <StudentCollegeInfoUI[]>JSON.parse(JSON.stringify(this.StudentCollegeRecords));
            this.StudentCollegeRecs.forEach(item => {
                item.Edited = false;
                item.OriginalCounselorSAPID = item.CounselorSAPID;
            });
        }
        if (!this.SCRecordDataSource) {
            this.SCRecordDataSource = new DataTableSource<StudentCollegeInfoUI>(this.StudentCollegeRecs);
        } else {
            this.SCRecordDataSource.Update(this.StudentCollegeRecs);
        }

        if (this.User) {
            this.AsAdvisor = this.User.IsAdvisor;
            this.AsCollegeAdmin = this.User.IsCollegeAdmin;
            this.AsCollegeSupport = this.User.IsCollegeSupport;
            this.AsCounselor = this.User.IsCounselor;
            this.AsDeveloper = this.User.IsDeveloper;
            this.AsDistrictAdmin = this.User.IsDistrictAdmin;
            this.AsParent = this.User.IsParent;
            this.AsStudent = this.User.IsStudent;
        }

        // Set up headers for display
        this.VisibleHeaders = [];

        if (this.SmallDisplay) {
            this.VisibleHeaders.push('SmallDisplay');
        } else {
            if (this.StudentCollegeRecs && this.StudentCollegeRecs.length > 1) {
                this.VisibleHeaders.push('Selection');
            }
            this.VisibleHeaders.push('CollegeName');
            if (!this.AsParent) {
                this.VisibleHeaders.push('DEProgram');
            }
            this.VisibleHeaders.push('Term', 'Counselor', 'Status');
            if (
                this.SelectedCollege &&
                this.SelectedCollege.HistoricalRecord !== true &&
                !this.AsAdvisor &&
                !this.AsCollegeAdmin &&
                !this.AsCollegeSupport
            ) {
                this.VisibleHeaders.push('Actions');
            }
        }

        this.NoData = !this.StudentCollegeRecs || !this.StudentCollegeRecs.length;
        this.ShownColumns = this.NoData ? ['NoData'] : this.SmallDisplay ? ['SmallDisplay'] : this.VisibleHeaders.slice();
    }

    // #region Add Student College Method

    AddStudentCollege(col: StudentCollegeInfo) {
        if (!col) {
            return;
        }

        this.add.emit(col);
    }

    // #endregion

    // #region Remove College Choice Method

    RemoveCollegeChoice(col: StudentCollegeInfo) {
        if (!col) {
            return;
        }

        // Emit the event
        this.remove.emit(col);
    }

    // #endregion

    // #region College Change Handler Method

    CollegeChangeHandler(col: StudentCollegeInfo) {
        if (!col) {
            return;
        }

        // Emit the event
        this.change.emit(col);
    }

    // #endregion

    // #region Cancel College Choice Method

    CancelCollegeChoice(col: StudentCollegeInfo) {
        if (!col) {
            return;
        }

        // Emit the event
        this.cancel.emit(col);
    }

    // #endregion

    // #region Cancel Dual Enrollment Program Change Handler Method

    CancelDualEnrollmentProgramChangeHandler(col: StudentCollegeInfo) {
        col.DualEnrollmentProgramID = col.OriginalDualEnrollmentProgramID;
        col.AcceleratedDegreeProgramID = col.OriginalAcceleratedDegreeProgramID;
    }

    // #endregion

    // #region Cancel Update Counselor Method

    CancelUpdateCounselor(col: StudentCollegeInfo) {
        col.CounselorSAPID = col.OriginalCounselorSAPID;
    }

    // #endregion

    // #region Dual Enrollment Program Change Handler Method

    DualEnrollmentProgramChangeHandler(col: StudentCollegeInfo) {
        if (!col) {
            return;
        }

        // Package up the new choice
        const packet: StudentProgramChange = {
            DualEnrollStudentID: col.DualEnrollStudentID,
            ProgramID: col.DualEnrollmentProgramID,
            AcceleratedDegreeProgramID: col.AcceleratedDegreeProgramID,
            OriginalProgramID: col.OriginalDualEnrollmentProgramID,
            OriginalAcceleratedDegreeProgramID: col.OriginalAcceleratedDegreeProgramID,
        };

        // Emit the event
        this.updateprogram.emit(packet);
    }

    // #endregion

    // #region Program Option Change

    ProgramOptionChange(event: MatSelectChange) {
        this.programChange.emit(event);
    }

    // #endregion

    // #region Select College Record Method

    SelectCollegeRecord(col: StudentCollegeInfo) {
        if (!col || col === this.SelectedCollege) {
            return;
        }

        // Emit the event
        this.select.emit(col);
    }

    // #endregion

    // #region Show Status Explanation Method

    ShowStatusExplanation(status: number) {
        const self = this;

        const explain = self.formStatusPipe.transform(status, true);

        // Pop up the dialog
        self.dialogService
            .OpenMessageBoxDialog({
                TitleText: 'Information',
                MessageText: explain,
                OKText: 'OK',
            })
            .subscribe(result => {});
    }

    // #endregion

    // #region Toggle Agreements Method

    ToggleAgreements(col: StudentCollegeInfoUI) {
        this.toggleAgreements.emit(col);
    }

    // #endregion

    // #region Update College Method

    UpdateCollege(col: StudentCollegeInfoUI) {
        // Check for changes
        let changed = false;

        if (col.CounselorSAPID !== col.OriginalCounselorSAPID) {
            changed = true;
        }

        col.Edited = changed;

        this.update.emit(col);
    }

    // #endregion
}

// #region Custom Classes

// #region Student College Info UI Class

export class StudentCollegeInfoUI extends StudentCollegeInfo {
    Edited?: boolean;

    constructor(info?: StudentCollegeInfo) {
        super();

        this.DualEnrollStudentID = info.DualEnrollStudentID;
        this.StudentID = info.StudentID;
        this.SchoolYear = info.SchoolYear;
        this.Term = <CollegeTermInfo>JSON.parse(JSON.stringify(info.Term));
        this.DateUpdated = info.DateUpdated ? parse(info.DateUpdated) : null;
        this.CollegeID = info.CollegeID;
        this.CollegeName = info.CollegeName;
        this.CollegeSchoolYear = info.CollegeSchoolYear;
        this.FormStatus = info.FormStatus;
        this.DualEnrollmentProgramID = info.DualEnrollmentProgramID;
        this.DualEnrollmentProgram = <ProgramInfo>JSON.parse(JSON.stringify(info.DualEnrollmentProgram));
        this.AcceleratedDegreeProgramID = info.AcceleratedDegreeProgramID;
        this.OriginalDualEnrollmentProgramID = info.DualEnrollmentProgramID;
        this.OriginalAcceleratedDegreeProgramID = info.AcceleratedDegreeProgramID;
        this.CounselorSAPID = info.CounselorSAPID;
        this.OriginalCounselorSAPID = info.OriginalCounselorSAPID;
        this.CounselorApproved = info.CounselorApproved;
        this.CounselorApprovedUser = info.CounselorApprovedUser;
        this.CounselorApprovedDate = info.CounselorApprovedDate ? parse(info.CounselorApprovedDate) : null;
        this.SchoolApproved = info.SchoolApproved;
        this.SchoolApprovedUser = info.SchoolApprovedUser;
        this.SchoolApprovedDate = info.SchoolApprovedDate ? parse(info.SchoolApprovedDate) : null;
        this.CollegeApproved = info.CollegeApproved;
        this.CollegeApprovedUser = info.CollegeApprovedUser;
        this.CollegeApprovedDate = info.CollegeApprovedDate ? parse(info.CollegeApprovedDate) : null;
        this.ParentHasApproved = info.ParentHasApproved;
        this.ParentApprovedUser = info.ParentApprovedUser;
        this.ParentApprovedDate = info.ParentApprovedDate ? parse(info.ParentApprovedDate) : null;
        this.AsProxy = info.AsProxy;
        this.ProxyParentName = info.ProxyParentName;
        this.ProxyRelationship = info.ProxyRelationship;
        this.ProxyDescription = info.ProxyDescription;
        this.StudentCollegeAgreementApproved = info.StudentCollegeAgreementApproved;
        this.StudentPolkAgreementApproved = info.StudentPolkAgreementApproved;
        this.StudentCollegeID = info.StudentCollegeID;
        this.AgreementText = info.AgreementText;
        this.AgreementTextDate = info.AgreementTextDate ? parse(info.AgreementTextDate) : null;
        this.AgreementConsentText = info.AgreementConsentText;
        this.AvailableTerms = <CollegeTermInfo[]>JSON.parse(JSON.stringify(info.AvailableTerms));
        this.HistoricalRecord = info.HistoricalRecord;
        this.FastTrackAdvised = info.FastTrackAdvised;
        this.Edited = false;
    }
}

// #endregion

// #region Student Program Change Info Class

export class StudentProgramChange extends ProgramInfo {
    DualEnrollStudentID?: number;
    OriginalProgramID?: number;
    OriginalAcceleratedDegreeProgramID?: number;
}

// #endregion
// #endregion



// WEBPACK FOOTER //
// ./src/app/ui/studentcollegerecords/studentcollegerecords.component.ts